import { FaTooth } from 'react-icons/fa';
import {
  AProgress,
  ATable,
  PageIntro,
  PagePanel,
  DropdownOptions,
} from '../../components';
import {
  MenuProps,
  Button,
  Dropdown,
  Input,
  Segmented,
  Tooltip,
  Tag,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  BarsOutlined,
  EditOutlined,
  ExportOutlined,
  LineChartOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  RedoOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { currency } from '../../shared/utils/functions';
import {
  ApprovalContainer,
  PendentContainer,
  TreatmentsFilterCard,
  TreatmentsLayout,
  TreatmentsStatItem,
  TreatmentsStatsCard,
  TreatmentsStatsGrid,
  TreatmentsSummaryGrid,
  TreatmentsToolbar,
} from '../Treatments/styles';
import { getDentalTreatments } from '../../http';
import { IDentalTreatment } from '../../shared/interfaces';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Dental: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [treatments, setTreatments] = useState<IDentalTreatment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [type, setType] = useState<number | null>(null);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const totalTreatments = treatments.length;
  const approved = treatments.filter((t) => t.approval === true).length;
  const pending = treatments.filter((t) => t.approval === null).length;
  const inProgress = treatments.filter((t) => Number(t.progress) > 0).length;

  const renderEllipsis = (value: string) => (
    <Tooltip title={value}>
      <span
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </span>
    </Tooltip>
  );

  const menuItems: MenuProps['items'] = [
    {
      label: t('dentalPage.actions.refresh'),
      key: '1',
      icon: <RedoOutlined />,
      onClick: () => listDentalTreatments(),
    },
    ...(!client_id
      ? permissionFind([PermissionActions.dentalTreatmentsCreate])
        ? [
            {
              label: t('dentalPage.actions.create'),
              key: '2',
              icon: <PlusCircleOutlined />,
              onClick: () => navigate('/dental/new'),
            },
          ]
        : []
      : []),
  ];

  const listDentalTreatments = useCallback(async () => {
    setLoading(true);
    setTreatments([]);

    try {
      const response = await getDentalTreatments(
        client_id,
        limit,
        (page - 1) * limit,
        search,
        type,
      );
      setPages(response.count);
      setTreatments(response.data);
    } catch {
      addToast({
        type: 'error',
        title: t('dentalPage.toast.errorTitle'),
        description: t('dentalPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [client_id, limit, search, page, type, addToast, t]);

  useEffect(() => {
    listDentalTreatments();
  }, [page, search, limit, listDentalTreatments]);

  return (
    <TreatmentsLayout>
      <PageIntro
        badge={t('dentalPage.intro.badge')}
        icon={<FaTooth />}
        title={t('dentalPage.intro.title')}
        description={t('dentalPage.intro.description')}
        actions={
          !client_id ? (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button icon={<BarsOutlined />} type="primary">
                {t('dentalPage.actions.moreActions')}
              </Button>
            </Dropdown>
          ) : undefined
        }
      />

      <PagePanel>
        <TreatmentsToolbar>
          <div
            style={{
              flex: 1,
              minWidth: 280,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {client_id && (
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                {t('dentalPage.actions.back')}
              </Button>
            )}
            <Input
              placeholder={t('dentalPage.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              onPressEnter={listDentalTreatments}
              allowClear
              style={{ flex: 1, minWidth: 240 }}
            />
          </div>

          {client_id ? (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button icon={<BarsOutlined />}>
                {t('dentalPage.actions.moreActions')}
              </Button>
            </Dropdown>
          ) : null}
        </TreatmentsToolbar>

        <TreatmentsSummaryGrid>
          <TreatmentsStatsCard>
            <TreatmentsStatsGrid>
              <TreatmentsStatItem>
                <span className="label">{t('dentalPage.stats.total')}</span>
                <span className="value">{totalTreatments}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">{t('dentalPage.stats.approved')}</span>
                <span className="value success">{approved}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">{t('dentalPage.stats.pending')}</span>
                <span className="value warning">{pending}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">
                  {t('dentalPage.stats.inProgress')}
                </span>
                <span className="value info">{inProgress}</span>
              </TreatmentsStatItem>
            </TreatmentsStatsGrid>
          </TreatmentsStatsCard>

          <TreatmentsFilterCard>
            <Segmented
              value={type === null ? 'all' : String(type)}
              onChange={(val) => {
                setPage(1);
                setType(val === 'all' ? null : Number(val));
              }}
              options={[
                { label: t('dentalPage.filters.all'), value: 'all' },
                { label: t('dentalPage.filters.budgets'), value: '0' },
                { label: t('dentalPage.filters.treatments'), value: '1' },
              ]}
            />
          </TreatmentsFilterCard>
        </TreatmentsSummaryGrid>
      </PagePanel>

      <PagePanel>
        <ATable
          scroll={{ x: 1100, y: 510 }}
          columns={[
            {
              title: t('dentalPage.table.id'),
              width: 80,
              dataIndex: 'code',
            },
            {
              title: t('dentalPage.table.type'),
              width: 70,
              dataIndex: 'type',
              render: (value: number) => (
                <>
                  {value > 0
                    ? t('dentalPage.table.typeTreatmentShort')
                    : t('dentalPage.table.typeBudgetShort')}
                </>
              ),
            },
            {
              title: t('dentalPage.table.client'),
              dataIndex: 'client_name',
              width: 240,
              render: (value: string) => renderEllipsis(value),
            },
            {
              title: t('dentalPage.table.professional'),
              dataIndex: 'professional_name',
              width: 220,
              render: (value: string) => renderEllipsis(value),
            },
            {
              title: t('dentalPage.table.total'),
              width: 120,
              render: (cell: any) => (
                <>{currency(cell.total - cell.discount)}</>
              ),
            },
            {
              title: t('dentalPage.table.progress'),
              dataIndex: 'progress',
              width: 180,
              render: (value: number) => <AProgress percent={value} />,
            },
            {
              title: t('dentalPage.table.status'),
              dataIndex: 'approval',
              width: 120,
              render: (value: boolean | null) => (
                <>
                  {value !== null ? (
                    <ApprovalContainer success={value}>
                      {value
                        ? t('dentalPage.status.approved')
                        : t('dentalPage.status.rejected')}
                    </ApprovalContainer>
                  ) : (
                    <PendentContainer>
                      {t('dentalPage.status.pending')}
                    </PendentContainer>
                  )}
                </>
              ),
            },
            {
              title: t('dentalPage.table.signature'),
              width: 120,
              render: (cell: IDentalTreatment) => {
                const isSigned = Boolean(
                  cell.signature_validation_code || cell.signed_at,
                );

                return (
                  <Tag color={isSigned ? 'green' : 'warning'}>
                    {isSigned
                      ? t('dentalPage.signature.signed')
                      : t('dentalPage.signature.notSigned')}
                  </Tag>
                );
              },
            },
            {
              title: t('dentalPage.table.actions'),
              width: 130,
              fixed: 'right' as const,
              render: (cell: any) => {
                return (
                  <DropdownOptions
                    items={[
                      permissionFind([PermissionActions.dentalTreatmentsUpdate])
                        ? {
                            label: cell.approval
                              ? t('dentalPage.actions.view')
                              : t('dentalPage.actions.approveOrReject'),
                            key: '1',
                            icon: <ExportOutlined />,
                            onClick: () =>
                              navigate(`/dental/${cell.id}/approval`),
                          }
                        : null,
                      permissionFind([PermissionActions.dentalTreatmentsUpdate])
                        ? {
                            label: t('dentalPage.actions.edit'),
                            key: '2',
                            icon: <EditOutlined />,
                            onClick: () => navigate(`/dental/${cell.id}`),
                            disabled: cell.approval,
                          }
                        : null,
                      cell.type === 1 &&
                      cell.approval &&
                      permissionFind([PermissionActions.dentalTreatmentsUpdate])
                        ? {
                            label: t('dentalPage.actions.evolution'),
                            key: '3',
                            icon: <LineChartOutlined />,
                            onClick: () =>
                              navigate(`/dental/${cell.id}/evolution`),
                          }
                        : null,
                      {
                        label: t('dentalPage.actions.print'),
                        key: '4',
                        icon: <PrinterOutlined />,
                        onClick: () => navigate(`/dental/${cell.id}/print`),
                      },
                    ]}
                  />
                );
              },
            },
          ]}
          loading={loading}
          dataSource={treatments}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </TreatmentsLayout>
  );
};

export default Dental;
